import React, { useEffect, useState } from 'react';
import { MemoryRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import TicketingLanding from './TicketingLanding';
import PassPicker from './PassPicker';
import BookingForm from './BookingForm';
import { FRAME, HOST, PreviewContext, visibleTo } from '../../utils/ticketingPreview';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } } });
const origin = window.location.origin;
const toHost = (message) => window.parent?.postMessage({ source: FRAME, ...message }, origin);

/** Keeps the admin page's page picker and the preview's in-memory route in step. */
const Bridge = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== origin || e.data?.source !== HOST || e.data.type !== 'navigate') return;
      navigate(e.data.path);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigate]);
  useEffect(() => {
    window.scrollTo(0, 0);
    toHost({ type: 'route', path: pathname });
  }, [pathname]);
  return null;
};

const Book = ({ view, eventRef }) => {
  const { passId } = useParams();
  return <BookingForm key={passId} view={{ ...view, ticketing: visibleTo(view.ticketing, passId) }} eventRef={eventRef} passId={passId} />;
};

const Passes = ({ view, eventRef }) => {
  const { groupId } = useParams();
  return <PassPicker key={groupId} view={{ ...view, ticketing: visibleTo(view.ticketing) }} eventRef={eventRef} groupId={groupId} />;
};

/** Rendered at /preview/ticketing inside the admin editor's iframe; the draft arrives by postMessage. */
const TicketingPreview = () => {
  const [view, setView] = useState(null);
  const [start, setStart] = useState(null);

  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== origin || e.data?.source !== HOST || e.data.type !== 'view') return;
      setView(e.data.view);
      setStart((current) => current || e.data.path || null);
    };
    window.addEventListener('message', onMessage);
    toHost({ type: 'ready' });
    return () => window.removeEventListener('message', onMessage);
  }, []);

  if (!view) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center text-sm text-gray-500">
        {window.parent === window ? 'Open the preview from the Premium registration settings.' : 'Loading preview…'}
      </div>
    );
  }
  const eventRef = view.slug || view.eventId;
  const base = `/register/${eventRef}`;
  return (
    <QueryClientProvider client={queryClient}>
      <PreviewContext.Provider value>
        <MemoryRouter initialEntries={[start?.startsWith(`${base}`) ? start : base]}>
          <Bridge />
          <Routes>
            <Route path="/register/:eventId" element={<TicketingLanding view={{ ...view, ticketing: visibleTo(view.ticketing) }} eventRef={eventRef} />} />
            <Route path="/register/:eventId/passes/:groupId" element={<Passes view={view} eventRef={eventRef} />} />
            <Route path="/register/:eventId/book/:passId" element={<Book view={view} eventRef={eventRef} />} />
            <Route path="*" element={<Navigate to={base} replace />} />
          </Routes>
        </MemoryRouter>
      </PreviewContext.Provider>
    </QueryClientProvider>
  );
};

export default TicketingPreview;
